export const PREFIX = 'lsh';

const INTERNAL_ERROR = "INTERNAL_ERROR";
const EXTERNAL_ERROR = "EXTERNAL_ERROR";

const INTERNAL_DOMAINS = [
    "localhost",
    window?.Shopify?.shop || 'lashifyusa.myshopify.com',
    window?.Shopify?.domain,
    window?.Shopify?.domain?.replace("www.", ""),
];

let bugsnag = null;
const pendingCalls = [];
const pendingQueueErrors = [];

const enqueueOrCall = (method, args) => {
    if (bugsnag) {
        bugsnag[method]?.(...args);
        return;
    }

    pendingCalls.push({ method, args });
};

// Run low-value, non-urgent work (breadcrumbs) off the critical path. Prefer
// scheduler.postTask at 'background' priority where available, falling back to
// requestIdleCallback and finally a microtask-ish setTimeout. Background tasks
// run FIFO within their priority, so breadcrumb ordering is preserved.
const scheduleBackground = (callback) => {
    if (typeof scheduler !== 'undefined' && typeof scheduler?.postTask === 'function') {
        scheduler.postTask(callback, { priority: 'background' });
    } else if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(callback, { timeout: 1000 });
    } else {
        setTimeout(callback, 0);
    }
};

const proxy = {
    notify: (...args) => enqueueOrCall('notify', args),
    leaveBreadcrumb: (...args) =>
        scheduleBackground(() => enqueueOrCall('leaveBreadcrumb', args)),
};

// Cheap, O(1) replacements for Bugsnag's recursive getNodeSelector/getNodeText.
// We only ever read a single element, never walk ancestors or run querySelectorAll.
const cheapNodeSelector = (el) => {
    if (!el || !el.tagName) {
        return '';
    }

    let selector = el.tagName.toLowerCase();
    if (el.id) {
        selector += `#${el.id}`;
    } else if (typeof el.className === 'string' && el.className.trim()) {
        // className is an SVGAnimatedString (object) on SVG nodes — the typeof
        // guard skips those rather than throwing.
        selector += `.${el.className.trim().split(/\s+/)[0]}`;
    }

    return selector;
};

const cheapNodeText = (el) => {
    const text = (el?.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > 140 ? `${text.slice(0, 135)}(...)` : text;
};

// Replacement for the disabled built-in 'user' breadcrumb: passive so it can't
// block the click, and all DOM reads happen inside a background task, keeping
// the click's EventDispatch/processing time free of breadcrumb work.
window.addEventListener(
    'click',
    (event) => {
        const target = event.target;
        if (!target) {
            return;
        }

        scheduleBackground(() => {
            try {
                enqueueOrCall('leaveBreadcrumb', [
                    'UI click',
                    {
                        targetText: cheapNodeText(target),
                        targetSelector: cheapNodeSelector(target),
                    },
                    'user',
                ]);
            } catch (error) {
                console.warn(`BUGSNAG_CLICK_BREADCRUMB_ERROR: ${error}`);
            }
        });
    },
    { capture: true, passive: true }
);

const customReport = (error) => {
    fetch(`https://generic-logger.arseniy-85b.workers.dev/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            message: error?.message,
        }),
        mode: 'no-cors',
    }).catch((error) => {
        console.warn(`GENERIC_LOGGER_ERROR: ${error}`);
    });
};

const drainQueueArray = (queue) => {
    if (!Array.isArray(queue) || !queue.length) {
        return;
    }

    queue.forEach((entry) => {
        if (!entry) {
            return;
        }

        pendingQueueErrors.push(entry);
        customReport(entry.error);
    });
};

drainQueueArray(window.bugsnagQueue);

window.bugsnagQueue = {
    push: ({ error, onError = () => {} } = {}) => {
        if (bugsnag) {
            bugsnag.notify(error, onError);
        } else {
            pendingQueueErrors.push({ error, onError });
        }

        customReport(error);
    },
};

let bugsnagStarting = false;

const startBugsnag = async () => {
    if (bugsnag || bugsnagStarting) {
        return;
    }

    bugsnagStarting = true;

    // Lazy-load the Bugsnag SDK only once an interaction (or the idle fallback)
    // fires, so @bugsnag/js stays out of the eager every-page bundle that
    // theme.js pulls in via helpers/klaviyo, helpers/video and api/cart. The
    // default `proxy` export keeps queueing notify/leaveBreadcrumb calls until
    // this resolves, so all synchronous importers are unaffected.
    let Bugsnag;
    try {
        ({ default: Bugsnag } = await import('@bugsnag/js'));
    } catch (error) {
        bugsnagStarting = false;
        console.warn(`BUGSNAG_LOAD_ERROR: ${error}`);
        return;
    }

    bugsnag = Bugsnag.start({
        autoTrackSessions: true,
        appVersion: import.meta.env.VITE_BUGSNAG_APP_VERSION || 'development',
        apiKey:
            import.meta.env.VITE_BUGSNAG_API_KEY ||
            'afb7ea98e0144614ba31260a293dd6c9',
        otherOptions: {
            appType: 'frontend-minicart',
        },
        trackInlineScripts: false,
        enabledReleaseStages: [
            'production',
            'staging'
        ],
        releaseStage: import.meta.env.MODE || "production",
        user: {
            id: window?.Shopify?.customer?.id || null,
            email: window?.Shopify?.customer?.email || null,
            name: window?.Shopify?.customer?.name || null,
        },
        metadata: {
            theme: {
                id: window?.Shopify?.theme?.id || null,
                name: window?.Shopify?.theme?.name || null,
            },
        },
        maxBreadcrumbs: 20,
        maxEvents: 20,
        // Drop Bugsnag's built-in 'user' breadcrumbs. Its interactionBreadcrumbs
        // plugin attaches a capture-phase window 'click' listener that runs
        // getNodeSelector() synchronously inside every click's EventDispatch —
        // a recursive document.querySelectorAll walk up the parent chain that
        // cost ~148ms on deep PDP/product-card DOMs. We re-add click breadcrumbs
        // below via a passive listener that does the work off the critical path.
        enabledBreadcrumbTypes: [
            'navigation',
            'request',
            'process',
            'log',
            'state',
            'error',
            'manual',
        ],
        onError(event) {
            event?.addMetadata('customer', {
                id: window?.Shopify?.customer?.id || null,
                email: window?.Shopify?.customer?.email || null,
                name: window?.Shopify?.customer?.name || null,
            });

            (() => {
                const types = [
                    ...new Set(
                        event?.errors?.reduce((errors, error) => {
                            return [
                                ...errors,
                                ...(error?.stacktrace?.map((trace) => {
                                    const file = trace?.file;
                                    if (!file) {
                                        return INTERNAL_ERROR;
                                    }

                                    if (
                                        ![
                                            !!INTERNAL_DOMAINS?.map((domain) => {
                                                return !![
                                                    `https://${domain}`,
                                                    `http://${domain}`,
                                                    `//${domain}`,
                                                ]
                                                    ?.map((url) => {
                                                        return file?.startsWith(
                                                            url
                                                        );
                                                    })
                                                    ?.includes(true);
                                            })?.includes(true),
                                            // another conditions
                                        ]?.includes(false)
                                    ) {
                                        return INTERNAL_ERROR;
                                    }

                                    return EXTERNAL_ERROR;
                                }) || []),
                            ];
                        }, []) || []
                    ),
                ];

                event?.addMetadata('errorType', {
                    types,
                    internal: !!types?.includes(INTERNAL_ERROR) ? 1 : 0,
                    external: !!types?.includes(EXTERNAL_ERROR) ? 1 : 0,
                });
            })();

            (() => {
                const searchParams = new URLSearchParams(window?.location?.search);
                if (!searchParams) {
                    event?.addMetadata('kinn', {
                        isKinn: 0,
                    });

                    return;
                }

                if (!searchParams?.get('app')?.startsWith('kin')) {
                    event?.addMetadata('kinn', {
                        isKinn: 0,
                    });

                    return;
                }

                event?.addMetadata('kinn', {
                    isKinn: 1,
                });
            })();

            (() => {
                event?.addMetadata('errorStacks', {
                    stack:
                        event?.errors?.reduce((stacks, error) => {
                            if (error instanceof TypeError) {
                                return [...stacks, error.stack];
                            }

                            if (error instanceof SyntaxError) {
                                return [...stacks, error.stack];
                            }

                            return stacks;
                        }, []) || [],
                });
            })();
        },
    });

    while (pendingCalls.length) {
        const { method, args } = pendingCalls.shift();
        bugsnag[method]?.(...args);
    }

    while (pendingQueueErrors.length) {
        const { error, onError = () => {} } = pendingQueueErrors.shift();
        bugsnag.notify(error, onError);
    }
};

const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

function triggerStart() {
    interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, triggerStart, { capture: true });
    });
    startBugsnag();
}

interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, triggerStart, {
        capture: true,
        once: true,
        passive: true,
    });
});

if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(triggerStart, { timeout: 3000 });
} else {
    setTimeout(triggerStart, 3000);
}

window.addEventListener('unhandledrejection', (event) => {
    console.warn(`UNHANDLED PROMISE REJECTION: ${event.reason}`);
});

export default proxy;
